import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";

export interface AdminWorkspace {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  primaryEmail: string | null;
  primaryPhone: string | null;
  address: string | null;
  logoUrl: string | null;
  whatriteEnabled: boolean;
  whatmailEnabled: boolean;
  salesAutoClaim: boolean;
  formsApiKey: string | null;
  metaAppId: string | null;
  metaAppSecret: string | null;
  metaGraphApiVersion: string;
  whatmailConfig: unknown;
  whatriteConfig: unknown;
  reconciliationCron: string;
  reconciliationEnabled: boolean;
}

export interface AdminPage {
  id: string;
  pageId: string;
  pageName: string | null;
  formIds: string[];
  isActive: boolean;
  subscribedAt: string | null;
  pageAccessToken: string;
  verifyToken: string;
}

interface ListResponse { workspaces: AdminWorkspace[] }
interface DetailResponse { workspace: AdminWorkspace; pages: AdminPage[] }

export function useAdminWorkspaces() {
  return useQuery({
    queryKey: ["admin", "workspaces"],
    queryFn: () => apiClient.get<ListResponse>("/api/superadmin/workspaces"),
  });
}

export function useAdminWorkspace(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ["admin", "workspace", id],
    queryFn: () => apiClient.get<DetailResponse>(`/api/superadmin/workspaces/${id}`),
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      slug: string;
      name: string;
      timezone?: string;
      metaAppId?: string;
      metaAppSecret?: string;
    }) =>
      apiClient.post<{ workspace: AdminWorkspace; formsApiKey: string }>(
        "/api/superadmin/workspaces",
        { json: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

export function useUpdateWorkspaceToggles(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      whatriteEnabled?: boolean;
      whatmailEnabled?: boolean;
      salesAutoClaim?: boolean;
    }) =>
      apiClient.patch<{ workspace: AdminWorkspace }>(
        `/api/superadmin/workspaces/${workspaceId}/toggles`,
        { json: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "workspace", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

export function useUpdateReconciliation(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      reconciliationCron?: string;
      reconciliationEnabled?: boolean;
    }) =>
      apiClient.patch<{ workspace: AdminWorkspace }>(
        `/api/superadmin/workspaces/${workspaceId}/reconciliation`,
        { json: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "workspace", workspaceId] });
    },
  });
}

export function useUpdateWorkspaceProfile(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      primaryEmail?: string | null;
      primaryPhone?: string | null;
      address?: string | null;
      logoUrl?: string | null;
    }) =>
      apiClient.patch<{ workspace: AdminWorkspace }>(
        `/api/superadmin/workspaces/${workspaceId}/profile`,
        { json: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "workspace", workspaceId] });
    },
  });
}

export function useUpdateWorkspaceConfig(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { whatmailConfig?: unknown; whatriteConfig?: unknown }) =>
      apiClient.patch<{ workspace: AdminWorkspace }>(
        `/api/superadmin/workspaces/${workspaceId}/config`,
        { json: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "workspace", workspaceId] });
    },
  });
}

export function useInviteToWorkspace(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: "sales" | "manager" | "admin" }) =>
      apiClient.post<{ inviteId: string; acceptUrl: string }>(
        `/api/superadmin/workspaces/${workspaceId}/invites`,
        { json: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["workspaces", workspaceId, "invites"] });
    },
  });
}

export interface PendingInvite {
  id: string;
  email: string;
  role: string;
  invitedBy: string;
  expiresAt: string;
  createdAt: string;
}

export function usePendingInvites(workspaceId: string | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ["workspaces", workspaceId, "invites"],
    queryFn: () =>
      apiClient.get<{ invites: PendingInvite[] }>(
        `/api/workspaces/${workspaceId}/invites`
      ),
  });
}

export function useRevokeInvite(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) =>
      apiClient.delete<void>(
        `/api/workspaces/${workspaceId}/invites/${inviteId}`
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["workspaces", workspaceId, "invites"] });
    },
  });
}

export function useReconcileWorkspace(workspaceId: string) {
  return useMutation({
    mutationFn: () =>
      apiClient.post<{ jobId: string | null }>(
        `/api/superadmin/workspaces/${workspaceId}/reconcile`
      ),
  });
}

export function useDeleteWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) =>
      apiClient.delete<void>(`/api/superadmin/workspaces/${workspaceId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

export function useUpdatePageForms(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { pageId: string; formIds: string[] }) =>
      apiClient.patch<{
        page: { id: string; pageId: string; pageName: string | null; formIds: string[] };
      }>(
        `/api/superadmin/workspaces/${workspaceId}/pages/${input.pageId}/forms`,
        { json: { formIds: input.formIds } }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "workspace", workspaceId] });
    },
  });
}

export function useDeleteWorkspacePage(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) =>
      apiClient.delete<void>(
        `/api/superadmin/workspaces/${workspaceId}/pages/${pageId}`
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "workspace", workspaceId] });
    },
  });
}

export interface AuditLogEntry {
  id: string;
  actorUserId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  metadata: unknown;
  createdAt: string;
}

export function useAuditLog(workspaceId: string | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ["admin", "audit-log", workspaceId],
    queryFn: () =>
      apiClient.get<{ entries: AuditLogEntry[]; limit: number; offset: number }>(
        `/api/superadmin/workspaces/${workspaceId}/audit-log`,
        { params: { limit: 100 } }
      ),
  });
}

export interface ActivityPreset {
  id: string;
  type: "note" | "call" | "meeting" | "email" | "whatsapp";
  label: string;
  outcome: string | null;
  suggestedNextStatus: string | null;
  sortOrder: number;
  isActive: boolean;
}

export function useActivityPresets(workspaceId: string | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ["workspaces", workspaceId, "activity-presets"],
    queryFn: () =>
      apiClient.get<{ presets: ActivityPreset[] }>(
        `/api/workspaces/${workspaceId}/activity-presets`
      ),
  });
}

export function useCreateActivityPreset(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      type: ActivityPreset["type"];
      label: string;
      outcome?: string;
      suggestedNextStatus?: string;
      sortOrder?: number;
    }) =>
      apiClient.post<{ preset: ActivityPreset }>(
        `/api/workspaces/${workspaceId}/activity-presets`,
        { json: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["workspaces", workspaceId, "activity-presets"],
      });
    },
  });
}

export function useUpdateActivityPreset(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & Partial<Omit<ActivityPreset, "id">>) => {
      const { id, ...patch } = input;
      return apiClient.put<{ preset: ActivityPreset }>(
        `/api/workspaces/${workspaceId}/activity-presets/${id}`,
        { json: patch }
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["workspaces", workspaceId, "activity-presets"],
      });
    },
  });
}

export function useDeleteActivityPreset(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<void>(
        `/api/workspaces/${workspaceId}/activity-presets/${id}`
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["workspaces", workspaceId, "activity-presets"],
      });
    },
  });
}

export function useAttachPage(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      pageId: string;
      pageName?: string;
      pageAccessToken: string;
      formIds: string[];
    }) =>
      apiClient.post<{ page: AdminPage & { verifyToken: string } }>(
        `/api/superadmin/workspaces/${workspaceId}/pages`,
        { json: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "workspace", workspaceId] });
    },
  });
}

export interface DiscoveredPage {
  id: string;
  name: string;
  access_token: string;
  _via?: string;
}

export function useExchangeToken() {
  return useMutation({
    mutationFn: (input: { app_id: string; app_secret: string; short_token: string }) =>
      apiClient.post<{ long_lived_token: string }>("/api/superadmin/meta/token/exchange", {
        json: input,
      }),
  });
}

export function useDiscoverPages() {
  return useMutation({
    mutationFn: (input: { long_lived_token: string }) =>
      apiClient.post<{ pages: DiscoveredPage[] }>("/api/superadmin/meta/pages/discover", {
        json: input,
      }),
  });
}

export function useSubscribePage() {
  return useMutation({
    mutationFn: (input: { page_id: string; page_access_token: string }) =>
      apiClient.post("/api/superadmin/meta/pages/subscribe", { json: input }),
  });
}

export interface LeadForm {
  id: string;
  name: string;
  status?: string;
  leads_count?: number;
}

export function useLeadForms(pageId: string | null, token: string | null) {
  return useQuery({
    enabled: !!pageId && !!token,
    queryKey: ["admin", "lead-forms", pageId],
    queryFn: () =>
      apiClient.get<{ forms: LeadForm[] }>(`/api/superadmin/meta/pages/${pageId}/lead-forms`, {
        params: { page_access_token: token ?? "" },
      }),
  });
}

export interface FeatureFlag {
  key: string;
  value: boolean;
  description: string | null;
  updatedAt: string;
}
export function useFeatureFlags() {
  return useQuery({
    queryKey: ["admin", "feature-flags"],
    queryFn: () => apiClient.get<{ flags: FeatureFlag[] }>("/api/superadmin/feature-flags"),
  });
}
export function useSetFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; value: boolean }) =>
      apiClient.post("/api/superadmin/feature-flags", { json: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "feature-flags"] });
    },
  });
}

export interface SystemTemplate {
  key: string;
  whatmailTemplateId: string;
  subjectOverride: string | null;
  isActive: boolean;
  updatedAt: string;
}
export function useSystemTemplates() {
  return useQuery({
    queryKey: ["admin", "system-templates"],
    queryFn: () => apiClient.get<{ templates: SystemTemplate[] }>("/api/superadmin/system-templates"),
  });
}
export function useUpsertSystemTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      key: string;
      whatmailTemplateId: string;
      subjectOverride?: string;
      isActive?: boolean;
    }) => apiClient.put("/api/superadmin/system-templates", { json: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "system-templates"] });
    },
  });
}
